// Singleton LiveBus: Push-Gegenpart zu resourceStore.
//
// resourceStore = Pull (get(key,fetcher) + subscribe-Replay). liveBus =
// Push: genau EINE Live-Verbindung (SSE / EventSource) pro Quell-URL,
// Fan-out nach SSE-Event-Name an alle Subscriber. Reconnect mit Backoff.
//
// Generic, tenant-agnostisch: liveBus parsed `data:` als JSON und reicht
// es 1:1 als T durch. KEIN Domain-Routing, KEIN Payload-Transform —
// liveBus liefert ALLE Events von `eventName` an ALLE Subscriber dieses
// Namens; Filterung (z.B. auf eine user-id) ist Sache des Konsumenten.
//
// track() ist ein vollstaendiges "current + future"-Primitiv mit
// optionalem same-shape Initial-Fetcher (siehe LiveTrackOptions): EIN
// Konsumenten-Handler bekommt erst den Initialwert, dann jedes Delta —
// in identischer T-Shape.

import { logger } from "../utils/debug-logger.js";

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;

export interface LiveTrackOptions<T> {
  // url: SSE-Quelle. Eine EventSource pro url wird geteilt.
  url: string;
  // eventName: SSE-Event-Name (EventSource.addEventListener). Alle Events
  // dieses Namens gehen an alle Subscriber dieses Namens.
  eventName: string;
  // key: Dedupe-Key fuer den Initial-Fetcher + Last-Initial-Replay.
  // Mehrere Subscriber mit gleichem key teilen sich einen Initial-Fetch.
  key: string;
  // initial: optionaler same-shape Initial-Fetcher. Weggelassen = reiner
  // Delta-Modus (die Quelle pusht den Initialwert selbst, z.B. Hub-
  // Snapshot-on-Connect). Gesetzt = self-sufficient (Standalone/Proxy).
  initial?: () => Promise<T>;
  // onValue: EIN Handler fuer Initialwert UND jedes Delta.
  onValue: (value: T) => void;
}

type Subscriber = (value: unknown) => void;

interface Connection {
  url: string;
  source: EventSource | null;
  // Subscriber pro SSE-Event-Name.
  subscribers: Map<string, Set<Subscriber>>;
  // Letztes Event pro SSE-Event-Name. Wird an spaet/re-gemountete
  // Subscriber sofort replayed (resourceStore.subscribe-Muster fuer
  // Push) — fixt z.B. einen via HTMX-OOB re-gemounteten
  // <pfs-user-presence>, der den IV-prime-Snapshot verpasst haette.
  // Generisch: Consumer filtert selbst (per id) → Replay nie falsch.
  lastEvents: Map<string, unknown>;
  // SSE-Event-Namen, fuer die bereits ein addEventListener haengt — muss
  // bei Reconnect auf die neue EventSource re-attached werden.
  listening: Set<string>;
  // Explizite connect()-Holds (z.B. <pfs-event-bridge>) zusaetzlich zur
  // Subscriber-Zahl. Verbindung lebt solange holds>0 ODER Subscriber>0.
  holds: number;
  reconnectAttempts: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
}

// Last-Initial-Cache + Inflight-Dedupe pro key (resourceStore.inflight-
// Muster). Nur der Initial-Fetcher schreibt hier — Deltas sind nicht
// generisch einem key zuordenbar (das waere Payload-Parsing).
interface InitialEntry {
  value?: unknown;
  has: boolean;
  inflight?: Promise<unknown>;
}

class LiveBus {
  private conns = new Map<string, Connection>();
  private initials = new Map<string, InitialEntry>();

  // connect oeffnet (idempotent) eine Verbindung und liefert eine
  // Release-Fn. Fuer rein deklarative Konsumenten (<pfs-event-bridge>),
  // die keine Subscriber sind aber die Leitung offen halten wollen.
  connect(url: string): () => void {
    const conn = this.ensure(url);
    conn.holds += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      conn.holds -= 1;
      this.maybeClose(conn);
    };
  }

  // track subscribed eventName + liefert (sofern vorhanden) erst den
  // Initialwert, dann jedes Delta — alles ueber onValue in T-Shape.
  // Returnt unsubscribe.
  track<T>(opts: LiveTrackOptions<T>): () => void {
    const { url, eventName, key, initial, onValue } = opts;
    const conn = this.ensure(url);
    conn.holds += 1;

    let set = conn.subscribers.get(eventName);
    if (!set) {
      set = new Set();
      conn.subscribers.set(eventName, set);
    }
    const sub = onValue as Subscriber;
    set.add(sub);
    this.attach(conn, eventName);

    // Last-Event-Replay (PFS-403): ein spaet/re-gemounteter Subscriber
    // bekommt sofort das letzte Event seines eventName — der via HTMX-
    // OOB-Swap neu erzeugte <pfs-user-presence> haette sonst den nur
    // einmalig beim SSE-Connect gefeuerten IV-prime-Snapshot verpasst
    // und bliebe grau bis zum naechsten Delta. Generisch: Consumer
    // filtert per id, Replay ist nie falsch.
    if (conn.lastEvents.has(eventName)) {
      onValue(conn.lastEvents.get(eventName) as T);
    }

    // Last-Initial-Replay: spaeter/zusaetzlich subscribende Konsumenten
    // mit gleichem key bekommen den Initialwert sofort, ohne Re-Fetch
    // (resourceStore.subscribe-Z.65-Muster). Loest N-Karten/late-mount —
    // KEIN Ersatz fuer den Initialwert beim Kaltstart.
    const cached = this.initials.get(key);
    if (cached?.has) {
      onValue(cached.value as T);
    } else if (initial) {
      void this.runInitial<T>(key, initial, onValue);
    }

    let unsubbed = false;
    return () => {
      if (unsubbed) return;
      unsubbed = true;
      set!.delete(sub);
      if (set!.size === 0) conn.subscribers.delete(eventName);
      conn.holds -= 1;
      this.maybeClose(conn);
    };
  }

  private runInitial<T>(
    key: string,
    initial: () => Promise<T>,
    onValue: (value: T) => void,
  ): Promise<void> {
    let entry = this.initials.get(key);
    if (!entry) {
      entry = { has: false };
      this.initials.set(key, entry);
    }
    if (entry.inflight) {
      return entry.inflight.then(
        (v) => onValue(v as T),
        () => {
          /* best-effort: Delta-Stream / Poll-Fallback deckt das ab */
        },
      );
    }
    const p = initial();
    entry.inflight = p;
    return p.then(
      (v) => {
        entry!.value = v;
        entry!.has = true;
        entry!.inflight = undefined;
        onValue(v);
      },
      (err) => {
        entry!.inflight = undefined;
        logger.warn("liveBus: initial fetcher rejected", { key, err });
      },
    );
  }

  private ensure(url: string): Connection {
    let conn = this.conns.get(url);
    if (!conn) {
      conn = {
        url,
        source: null,
        subscribers: new Map(),
        lastEvents: new Map(),
        listening: new Set(),
        holds: 0,
        reconnectAttempts: 0,
        reconnectTimer: null,
      };
      this.conns.set(url, conn);
      this.open(conn);
    }
    return conn;
  }

  private open(conn: Connection): void {
    if (typeof EventSource === "undefined") {
      logger.warn("liveBus: EventSource unavailable, push disabled", {
        url: conn.url,
      });
      return;
    }
    // PFS-406: eine waehrend `document.readyState === "loading"`
    // geoeffnete EventSource bricht der Browser beim Abschluss des
    // initialen Loads / bei Reload ab ("interrupted while loading"-
    // Console-Rauschen). Erst nach `load` oeffnen. Reconnect laeuft
    // immer mit readyState=complete → kein Defer auf dem Pfad.
    if (
      typeof document !== "undefined" &&
      document.readyState === "loading"
    ) {
      const openWhenLoaded = () => {
        // Conn koennte zwischenzeitlich abgebaut worden sein.
        if (this.conns.get(conn.url) !== conn) return;
        if (conn.holds === 0 && conn.subscribers.size === 0) return;
        if (conn.source) return;
        this.openNow(conn);
      };
      window.addEventListener("load", openWhenLoaded, { once: true });
      return;
    }
    this.openNow(conn);
  }

  private openNow(conn: Connection): void {
    const es = new EventSource(conn.url, { withCredentials: true });
    conn.source = es;
    es.addEventListener("open", () => {
      conn.reconnectAttempts = 0;
    });
    es.addEventListener("error", () => {
      // Native EventSource reconnected selbst solange readyState ==
      // CONNECTING. Nur den CLOSED-Fall mit Backoff selbst behandeln.
      if (es.readyState === EventSource.CLOSED) this.scheduleReconnect(conn);
    });
    for (const name of conn.listening) this.bind(conn, es, name);
  }

  private scheduleReconnect(conn: Connection): void {
    if (conn.reconnectTimer !== null) return;
    if (conn.holds === 0 && conn.subscribers.size === 0) return;
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** conn.reconnectAttempts,
      RECONNECT_CAP_MS,
    );
    conn.reconnectAttempts += 1;
    conn.reconnectTimer = setTimeout(() => {
      conn.reconnectTimer = null;
      if (conn.holds === 0 && conn.subscribers.size === 0) return;
      conn.source?.close();
      this.open(conn);
    }, delay);
  }

  private attach(conn: Connection, eventName: string): void {
    if (conn.listening.has(eventName)) return;
    conn.listening.add(eventName);
    if (conn.source) this.bind(conn, conn.source, eventName);
  }

  private bind(conn: Connection, es: EventSource, eventName: string): void {
    es.addEventListener(eventName, (ev: MessageEvent) => {
      let value: unknown;
      try {
        value = JSON.parse(ev.data as string);
      } catch (err) {
        logger.warn("liveBus: non-JSON SSE payload, skipped", {
          url: conn.url,
          eventName,
          err,
        });
        return;
      }
      // Vor dem Fan-out cachen — auch wenn (noch) kein Subscriber da
      // ist: der IV-prime-Snapshot trifft ein waehrend nur die
      // <pfs-event-bridge> die Connection haelt; ein spaeter
      // gemounteter <pfs-user-presence> bekommt ihn dann via Replay.
      conn.lastEvents.set(eventName, value);
      const subs = conn.subscribers.get(eventName);
      if (!subs) return;
      for (const cb of subs) cb(value);
    });
  }

  private maybeClose(conn: Connection): void {
    if (conn.holds > 0 || conn.subscribers.size > 0) return;
    if (conn.reconnectTimer !== null) {
      clearTimeout(conn.reconnectTimer);
      conn.reconnectTimer = null;
    }
    conn.source?.close();
    this.conns.delete(conn.url);
  }
}

export const liveBus = new LiveBus();
